import { MODAL_TRACKING } from "@/lib/constants/tracking";
import React, { forwardRef } from "react";

interface CustomInputProps {
  label?: string;
  type?: string;
  value?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  

  // handlers
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRightTextClick?: () => void;

  // left & right icons
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;

  // right side text button
  rightText?: string;

  // error message
  error?: string;

  className?: string;
  labelClassName?: string;
  trackingClassName?: string;
  testinputId?: string;
  autoFocus?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];

}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(function CustomInput({
  label,
  type = "text",
  placeholder = "",
  required = false,
  maxLength,
  minLength,
  value = "",
  onChange,
  onKeyDown,
  leftIcon,
  rightIcon,
  rightText,
  onRightTextClick,
  error,
  className = "",
  labelClassName = "",
  trackingClassName = "",
  testinputId = "custom-test-input",
  autoFocus = false,
  inputMode,
}, ref) {
  return (
    <label className={`block mb-4 w-full ${labelClassName}`}>
      {label && (
        <span className="block text-sm font-medium text-gray-700 pb-1">
          {label}
        </span>
      )}

      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            {leftIcon}
          </span>
        )}

        <input
          ref={ref}
          type={type}
          required={required}
          maxLength={maxLength}
          minLength={minLength}
          autoFocus={autoFocus}
          inputMode={inputMode}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={!label ? placeholder || undefined : undefined}
          className={`
            w-full p-2 border border-[#00000033] rounded-md text-[#90A1B9] leading-[28px] md:leading-[33px]
            focus:outline-none focus:border-[#008C8B] focus:ring-2 focus:ring-[#008C8B]/25
            ${
              error
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/25"
                : ""
            }
            ${leftIcon ? "pl-10" : ""}
            ${rightIcon || rightText ? "pr-[84px]" : ""}
            ${className} 
            ${trackingClassName}

          `}
          data-testid={testinputId}
        />

        {/* Right Icon */}
        {rightIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
            {rightIcon}
          </span>
        )}

        {/* Right Text Button */}
        {rightText && (
          <button
            type="button"
            onClick={onRightTextClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 text-sm hover:text-blue-700 cursor-pointer"
          >
            {rightText}
          </button>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </label>
  );
});

export default CustomInput;
